import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { INDIAN_STATES } from "@/lib/india";
import { districtsForState } from "@/lib/india_districts";

function SearchableCombobox({ value, onChange, options, placeholder, testid, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !value && "text-muted-foreground")}
          data-testid={testid}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[70vh]"
        onOpenAutoFocus={(e) => {
          // Allow the input to focus but do not steal scroll
          e.preventDefault();
        }}
      >
        <Command shouldFilter={true}>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}…`} />
          <CommandList
            className="max-h-[60vh] overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => { onChange(opt); setOpen(false); }}
                  data-testid={`${testid}-item-${opt}`}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === opt ? "opacity-100" : "opacity-0")} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Two linked searchable comboboxes for State + District.
 * When the state changes, the district resets automatically (unless the new
 * state also contains the current district).
 *
 * Props:
 *  - state, district: current values (strings)
 *  - onStateChange(v), onDistrictChange(v): change handlers
 *  - required: shows a red asterisk on labels
 *  - inline: renders both selects side-by-side (default). Pass false for stacked.
 */
export default function StateDistrictSelect({
  state = "",
  district = "",
  onStateChange,
  onDistrictChange,
  required = false,
  inline = true,
  testidPrefix = "sd",
}) {
  const districts = useMemo(() => districtsForState(state), [state]);

  const handleStateChange = (v) => {
    onStateChange?.(v);
    // Reset district if it no longer belongs to the new state
    const newDistricts = districtsForState(v);
    if (district && !newDistricts.includes(district)) {
      onDistrictChange?.("");
    }
  };

  return (
    <div className={inline ? "grid grid-cols-1 md:grid-cols-2 gap-3" : "space-y-3"}>
      <div className="space-y-1">
        <Label className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-muted-foreground" />
          State {required && <span className="text-red-500">*</span>}
        </Label>
        <SearchableCombobox
          value={state}
          onChange={handleStateChange}
          options={INDIAN_STATES}
          placeholder="Select state"
          testid={`${testidPrefix}-state`}
        />
      </div>
      <div className="space-y-1">
        <Label className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-muted-foreground" />
          District {required && <span className="text-red-500">*</span>}
        </Label>
        <SearchableCombobox
          value={district}
          onChange={onDistrictChange}
          options={districts}
          placeholder={state ? "Select district" : "Pick state first"}
          testid={`${testidPrefix}-district`}
          disabled={!state}
        />
      </div>
    </div>
  );
}
